import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { requestGetTransaction } from "../../Context/transaction/actions";
import {
  useTransactionDispatch,
  useTransactionState,
} from "../../Context/transaction/context";
import { useAuthState } from "../../Context";
import { useQuery } from "../../Utils/NavigationUtils";
import { Button } from "../../Components/Button";

const STATUS_LOADING = "LOADING";
const STATUS_SUCCESS = "SUCCESS";
const STATUS_PENDING = "PENDING";
const STATUS_FAILED = "FAILED";
const STATUS_ERROR = "ERROR";

function mapTransactionStatus(transactionStatus) {
  switch (transactionStatus) {
    case "SUCCESS":
      return STATUS_SUCCESS;
    case "FAILED":
    case "EXPIRED":
      return STATUS_FAILED;
    case "PENDING":
    default:
      return STATUS_PENDING;
  }
}

function mapParamStatus(paramStatus) {
  switch (paramStatus) {
    case "settlement":
    case "capture":
      return STATUS_SUCCESS;
    case "pending":
    case "authorize":
      return STATUS_PENDING;
    case "deny":
    case "cancel":
    case "expire":
    case "failure":
      return STATUS_FAILED;
    default:
      return STATUS_PENDING;
  }
}

const PaymentRedirect = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const query = useQuery();
  const dispatch = useTransactionDispatch();
  const { transaction, loading, error } = useTransactionState();
  const { user } = useAuthState();

  const orderId = query.get("order_id");
  const transactionStatus = query.get("transaction_status");

  const [status, setStatus] = useState(STATUS_LOADING);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (!orderId) {
      setStatus(STATUS_ERROR);
      setErrorMessage(t("Transaction ID not found."));
      return;
    }
    requestGetTransaction(dispatch, { id: orderId });
  }, [orderId, dispatch, t]);

  useEffect(() => {
    if (loading) return;
    if (status !== STATUS_LOADING) return;
    if (!user.id) return;

    if (error) {
      setStatus(STATUS_ERROR);
      setErrorMessage(t("Transaction not found."));
      return;
    }

    if (transaction && transaction.id) {
      if (transaction.userID !== user.id) {
        setStatus(STATUS_ERROR);
        setErrorMessage(t("Transaction not found."));
        return;
      }
      const transactionStatus = mapTransactionStatus(transaction.status);
      const paramStatus = mapParamStatus(transactionStatus);
      setStatus(
        paramStatus === transactionStatus && paramStatus !== null
          ? paramStatus
          : transactionStatus,
      );
    }
  }, [transaction, loading, error, user.id, status, t, transactionStatus]);

  useEffect(() => {
    if (status === STATUS_LOADING || status === STATUS_ERROR) return;

    const timer = setTimeout(() => {
      navigate(`/transaction/${orderId}`, { replace: true });
    }, 3000);

    return () => clearTimeout(timer);
  }, [status, orderId, navigate]);

  const statusConfig = {
    [STATUS_LOADING]: {
      icon: "spinner",
      title: t("Verifying payment"),
      description: t("Please wait while we verify your payment status."),
    },
    [STATUS_SUCCESS]: {
      icon: "check",
      title: t("Payment successful"),
      description: t("You will be redirected to transaction detail shortly."),
    },
    [STATUS_PENDING]: {
      icon: "clock",
      title: t("Payment is being processed"),
      description: t("You will be redirected to transaction detail shortly."),
    },
    [STATUS_FAILED]: {
      icon: "x",
      title: t("Payment failed"),
      description: t("You will be redirected to transaction detail shortly."),
    },
    [STATUS_ERROR]: {
      icon: "x",
      title: t("Error"),
      description: errorMessage,
    },
  };

  const config = statusConfig[status] || statusConfig[STATUS_LOADING];

  return (
    <div className="max-w-lg mx-auto">
      <div className="bg-white p-8 mt-6 mb-0 space-y-4 rounded-lg shadow-sm text-center">
        <div>
          {config.icon === "spinner" && (
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100">
              <svg
                role="status"
                className="w-5 h-5 animate-spin text-blue-600"
                viewBox="0 0 100 101"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                  fill="#E5E7EB"
                />
                <path
                  d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                  fill="currentColor"
                />
              </svg>
            </div>
          )}
          {config.icon === "check" && (
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6 text-green-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
          )}
          {config.icon === "clock" && (
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6 text-yellow-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
          )}
          {config.icon === "x" && (
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6 text-red-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
          )}
          <div className="mt-3 sm:mt-5">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              {config.title}
            </h3>
            <div className="mt-2">
              <p className="text-sm text-gray-500">{config.description}</p>
            </div>
          </div>
        </div>
        {status !== STATUS_LOADING && status !== STATUS_ERROR && orderId && (
          <div className="mt-5 sm:mt-6">
            <a href={`/transaction/${orderId}`}>
              <Button title={t("Go to Transaction Detail")} />
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentRedirect;
